// src/components/resources/glossary/TabContent.tsx

/**
 * TabContent Component
 * 
 * A comprehensive component for displaying different types of content in tabs for glossary terms.
 * Handles various term types (metrics, charts, creative, general) and their specific content needs.
 * Only shows tabs for content that exists for the given term.
 * 
 * Features:
 * - Dynamic tab rendering based on available content
 * - Animated transitions between tabs
 * - Support for definitions, calculations, examples, comparisons, usage guides, and limitations
 * - Rich content display including benchmarks, key points, and external resources
 * - Chart visualizations integrated into the definition tab for chart terms.
 * - Non-chart examples displayed in their own tab.
 * - Support for both direct fields and field content approach with backward compatibility
 * 
 * @module TabContent
 */

'use client'

import { useMemo, useState, useEffect, lazy, Suspense, memo, type ReactNode } from 'react'
import { motion } from 'motion/react'
import { clientLogger } from '~/lib/logger-client'
import RiExternalLinkLine from '~/icons/remix/generated/RiExternalLinkLine'
import RiArticleLine from '~/icons/remix/generated/RiArticleLine'
import RiVideoLine from '~/icons/remix/generated/RiVideoLine'
import RiToolsLine from '~/icons/remix/generated/RiToolsLine'
import RiFileLine from '~/icons/remix/generated/RiFileLine'
import RiLinksLine from '~/icons/remix/generated/RiLinksLine'
import RiBookmarkLine from '~/icons/remix/generated/RiBookmarkLine'
import RiQuestionLine from '~/icons/remix/generated/RiQuestionLine'
import RiCalculatorLine from '~/icons/remix/generated/RiCalculatorLine'
import RiBriefcaseLine from '~/icons/remix/generated/RiBriefcaseLine'
import RiBook2Line from '~/icons/remix/generated/RiBook2Line'
import RiCodeSSlashLine from '~/icons/remix/generated/RiCodeSSlashLine'
import RiFileList3Line from '~/icons/remix/generated/RiFileList3Line'
import RiFileTextLine from '~/icons/remix/generated/RiFileTextLine'
import { trackResourceLinkClick, createEngagementTimer } from '~/lib/gtag'
import type {
  GlossaryTerm,
  Example,
  MetricCalculation,
  ChartUseCase,
} from '~/lib/resources/types/glossary/terms'
import { isChartExample, isExampleWithContext, isChartTerm } from '~/lib/resources/types/glossary/terms'
import type { SupplementalResourceReference } from '~/lib/resources/types/shared/base'
import type { TabType } from '~/lib/resources/types/glossary/components'
import type { OperationType } from '~/lib/resources/types/shared/enums'
import type { MetricNetworkData } from '~/lib/resources/api/glossary/metrics'
import { 
  getUrlFromSupplementalResource, 
  isExternalSupplementalResource, 
  formatSupplementalResourceDisplayName,
  hasSupplementalResources,
  getBlogUrl
} from '~/lib/resources/utils/resourceUtils'
import MetricsComparison from '~/components/resources/glossary/MetricsCalculation'
import { NumberedListSection } from '~/components/resources/glossary/NumberedListSection'
import { FormulaCallout } from '~/components/resources/glossary/FormulaCallout'
import { FieldContentRenderer } from '~/components/resources/glossary/FieldContentRenderer'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { ErrorBoundary } from '~/components/shared/ErrorBoundary'

// Lazy load the MetricNetwork component
const MetricNetwork = lazy(() => import('./MetricNetwork').then(mod => ({ default: mod.MetricNetwork })))
// Import the GlossaryChart component using correct lazy syntax for named export
const GlossaryChartComponent = lazy(() => 
  import('~/components/charts/GlossaryChart').then(mod => ({ default: mod.GlossaryChart }))
)

/**
 * Props for the TabContent component
 * @interface TabContentProps
 */
interface TabContentProps {
  /** The glossary term to display content for */
  term: GlossaryTerm
  activeTab: TabType
  availableTabs: TabType[]
  /** Server-computed tab content availability */
  tabContent: Record<TabType, boolean>
  /** Pre-validated calculation data from server */
  calculationData?: MetricCalculation
  /** Pre-validated network data for metrics */
  networkData?: MetricNetworkData
}

/**
 * Displays key points about a term in a numbered list
 */
const KeyPointsSection = ({ points }: { points: string[] }): JSX.Element | null => (
  <NumberedListSection variant="card" heading="Key Points" items={points} color="emerald" />
)

/**
 * Displays best practices for implementing/using a term
 */
const BestPracticesSection = ({ practices }: { practices: string[] }): JSX.Element | null => (
  <NumberedListSection variant="card" heading="Best Practices" items={practices} color="indigo" />
)

/**
 * Displays supplemental resources with visually appealing cards
 */
const ResourcesSection = ({ 
  term 
}: { 
  term: GlossaryTerm
}): ReactNode => {
  // Create engagement timer for resource section
  const getEngagementMs = useMemo(() => createEngagementTimer(), [])
  
  // Handler for resource link clicks with GA4 tracking
  const handleResourceClick = (
    resource: SupplementalResourceReference,
    url: string
  ): void => {
    const isInternal = !isExternalSupplementalResource(resource)
    
    trackResourceLinkClick({
      linkType: isInternal ? 'internal' : 'external',
      linkUrl: url,
      linkTitle: resource.title,
      sourceResource: term.slug,
      sourceResourceType: 'glossary',
      linkCategory: resource.learningResourceType,
      engagementMs: getEngagementMs(),
      elementId: `glossary-${term.slug}-resource-${resource.learningResourceType}`
    })
  }
  
  if (!hasSupplementalResources(term)) return null
  
  // Type-safe extraction of supplementalResources
  // This type assertion is safe because hasSupplementalResources has already verified
  // that supplementalResources exists and is an array
  const supplementalResources = (term as GlossaryTerm & { 
    supplementalResources: SupplementalResourceReference[] 
  }).supplementalResources
  
  if (!supplementalResources.length) return null

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900/50">
      <h3 className="text-xl font-semibold mb-4">Additional Resources</h3>
      <ul className="space-y-4">
        {supplementalResources.map((resource: SupplementalResourceReference, index: number) => {
          // Use the utility function to determine if external
          const isInternal = !isExternalSupplementalResource(resource)
          
          // Get the full URL using the appropriate utility function based on resource type
          let url = ''
          if (isInternal) {
            if (resource.learningResourceType === 'article' || 
                resource.learningResourceType === 'article-collection' || 
                resource.blogTopic) {
              url = getBlogUrl(resource)
            } else {
              url = getUrlFromSupplementalResource(resource)
            }
          } else {
            url = getUrlFromSupplementalResource(resource)
          }
          
          return (
            <li
              key={index}
              className="group relative rounded-lg border transition-[transform,box-shadow] duration-200 hover:shadow-md motion-safe:hover:scale-[1.01] border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900"
            >
              <a
                href={url}
                target={isInternal ? undefined : "_blank"}
                rel={isInternal ? undefined : "noopener noreferrer"}
                className="block p-4"
                onClick={() => handleResourceClick(resource, url)}
              >
                <h4 className="font-medium text-gray-900 group-hover:text-indigo-600
                            dark:text-white dark:group-hover:text-indigo-400
                            transition-colors">
                  {resource.title}
                </h4>
                {resource.description && (
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    {resource.description}
                  </p>
                )}
                <div className="mt-2 flex items-center text-sm text-gray-500 dark:text-gray-500">
                  {getResourceIcon(resource, isInternal)}
                  {formatSupplementalResourceDisplayName(resource)}
                </div>
              </a>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

// Helper function to get resource icon
function getResourceIcon(
  resource: SupplementalResourceReference, 
  isInternal: boolean
): React.ReactNode {
  if (!isInternal) {
    return <RiExternalLinkLine className="mr-1 h-4 w-4" />
  }
  
  // Select icon based on resource type
  switch(resource.learningResourceType.toLowerCase()) {
    case 'article':
      return <RiArticleLine className="mr-1 h-4 w-4" />
    case 'video':
      return <RiVideoLine className="mr-1 h-4 w-4" />
    case 'tool':
      return <RiToolsLine className="mr-1 h-4 w-4" />
    case 'documentation':
      return <RiFileLine className="mr-1 h-4 w-4" />
    case 'guide':
      return <RiBookmarkLine className="mr-1 h-4 w-4" />
    case 'glossary':
      return <RiBookmarkLine className="mr-1 h-4 w-4" />
    case 'quiz':
      return <RiQuestionLine className="mr-1 h-4 w-4" />
    case 'calculator':
      return <RiCalculatorLine className="mr-1 h-4 w-4" />
    case 'case-study':
      return <RiBriefcaseLine className="mr-1 h-4 w-4" />
    case 'course':
      return <RiBook2Line className="mr-1 h-4 w-4" />
    case 'template':
      return <RiFileList3Line className="mr-1 h-4 w-4" />
    case 'tutorial':
      return <RiBook2Line className="mr-1 h-4 w-4" />
    case 'ebook':
      return <RiFileTextLine className="mr-1 h-4 w-4" />
    case 'generator':
      return <RiCodeSSlashLine className="mr-1 h-4 w-4" />
    case 'analyzer':
      return <RiToolsLine className="mr-1 h-4 w-4" />
    default:
      return <RiLinksLine className="mr-1 h-4 w-4" />
  }
}

/**
 * Displays best use cases for a term
 */
const BestUsedForSection = ({ useCases }: { useCases: string[] }): JSX.Element | null => (
  <NumberedListSection variant="card" heading="Best Used For" items={useCases} color="blue" />
)

/**
 * Displays chart usage guidelines in a styled list
 */
const ChartUsageSection = ({ items }: { items: string[] }): JSX.Element | null => (
  <NumberedListSection variant="card" heading="Usage Guidelines" items={items} color="blue" />
)

/**
 * Displays the "When to use this chart" section for chart terms.
 *
 * Deliberately distinct from BenchmarksTable: chart use-cases are NOT numeric
 * benchmark ranges, so they must NOT route through the
 * "Industry Benchmarks → Typical Range" table.
 * Each row pairs a marketing use case with the data shape it expects; the
 * source renders as a citation line and the note as a caveat.
 */
const ChartUseCasesSection = ({ useCases }: { useCases: ChartUseCase[] }): JSX.Element | null => {
  if (!useCases?.length) return null

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900/50">
      <h3 className="text-xl font-semibold mb-4">When to use this chart</h3>
      <ul className="space-y-4">
        {useCases.map((row, index) => (
          <li
            key={index}
            className="rounded-lg bg-indigo-50 p-4 dark:bg-indigo-900/20"
          >
            <p className="font-semibold text-indigo-900 dark:text-indigo-100">
              {row.useCase}
            </p>
            <p className="mt-1 text-indigo-800 dark:text-indigo-200">
              <span className="font-medium">Data shape:</span> {row.dataShape}
            </p>
            {row.note && (
              <p className="mt-1 text-sm italic text-indigo-700 dark:text-indigo-300">
                {row.note}
              </p>
            )}
            {row.source && (
              <p className="mt-1 text-xs text-indigo-600 dark:text-indigo-400">
                Source: {row.source}
              </p>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * Displays chart limitations in a styled list
 */
const ChartLimitationsSection = ({ items }: { items: string[] }): JSX.Element | null => (
  <NumberedListSection variant="card" heading="Limitations" items={items} color="rose" />
)

/**
 * Helper function to get field content from a term, supporting both approaches
 * 
 * @param {GlossaryTerm} term - The glossary term to get content from
 * @param {string} fieldName - The field name to retrieve
 * @returns {unknown} The content from either direct field or fieldContent
 */
function getTermContent(term: GlossaryTerm, fieldName: string): unknown {
  // First try direct field access for backward compatibility
  const directContent = term[fieldName as keyof GlossaryTerm]
  if (directContent !== undefined) {
    return directContent
  }
  
  // Then try fieldContent approach
  if (term.fieldContent?.[fieldName]) {
    return term.fieldContent[fieldName]
  }
  
  return undefined
}

/**
 * Get the text content from an example
 */
const getExampleText = (example: Example): string => {
  if (typeof example === 'string') return example
  if (isExampleWithContext(example)) return example.text
  if (isChartExample(example)) return example.description
  return ''
}

/**
 * A memoized component for rendering non-chart examples using the detailed layout.
 */
const ExampleItems = memo(({ examples, term }: { examples: Example[]; term: GlossaryTerm }): JSX.Element | null => {
  // Filter out chart examples as they are handled separately in the examples tab
  const nonChartExamples = examples.filter(ex => !isChartExample(ex))

  if (!nonChartExamples?.length) return null

  return (
    <>
      {nonChartExamples.map((example, index) => {
        const text = getExampleText(example)
        if (!text.trim()) return null

        return (
          <div
            key={`example-${index}-${term.slug ?? index}`}
            className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900/50 hover:border-indigo-200 dark:hover:border-indigo-800/50 transition-colors"
          >
            <div className="flex items-start gap-4">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400">
                {index + 1}
              </div>
              <div className="flex-1 space-y-1">
                <div className="prose dark:prose-invert max-w-none">
                  {text}
                </div>
                {isExampleWithContext(example) && example.context && (
                  <div className="mt-4 text-sm text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-800 pt-4">
                    <strong className="font-medium">Context: </strong>
                    {example.context}
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      })}
    </>
  )
})

// Set display name for debugging
ExampleItems.displayName = 'ExampleItems'

/**
 * Example rendering error fallback component
 */
const ExampleRenderingErrorFallback = ({ error }: { error: Error }): JSX.Element => (
  <div className="rounded-xl border border-red-200 bg-red-50 p-6 dark:border-red-900/30 dark:bg-red-900/10">
    <div className="flex items-start">
      <div className="flex-shrink-0">
        <ExclamationTriangleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
      </div>
      <div className="ml-3">
        <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Example Rendering Error</h3>
        <div className="mt-2 text-sm text-red-700 dark:text-red-300">
          <p>There was an error rendering these examples. Please try again later.</p>
          <p className="mt-1 text-xs">{error.message}</p>
        </div>
      </div>
    </div>
  </div>
)

/**
 * Main component for displaying tab content for glossary terms
 * Handles different content types and provides animated transitions between tabs
 * 
 * @param {TabContentProps} props - Component props
 * @returns {JSX.Element | null} The rendered tab content or null if invalid tab
 */
// Keep in sync with the `switch (activeTab)` cases in the content memo below.
// Used only to derive the "unhandled tab" diagnostic from an effect (a tab not
// matched by any case and with no fieldContent fallback).
const HANDLED_TAB_TYPES: ReadonlySet<string> = new Set([
  'definition',
  'calculation',
  'examples',
  'usage',
  'limitations',
  'advantages',
  'bestPractices',
  'bestUsedFor',
  'keyPoints',
  'comparison',
  'supplementalResources',
])

export function TabContent({
  term, 
  activeTab, 
  availableTabs, 
  tabContent,
  calculationData,
  networkData
}: TabContentProps): JSX.Element | null {
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot SSR hydration flag, set once after mount (client-only)
    setIsMounted(true)
  }, [])

  // Memoize content generation
  const content = useMemo(() => {
    if (!isMounted) return null

    // Early return if tab is not available or content doesn't exist
    if (!availableTabs.includes(activeTab) || !tabContent[activeTab]) {
      return null
    }

    // Handle standard tabs with specific logic FIRST
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch (activeTab) {
      case 'definition': {
        // Determine if it's a chart term with a valid example
        const isChartTermWithExample = 
          term.category === 'charts' && 
          tabContent.examples && // Check if example content *should* exist
          Array.isArray(term.examples) && 
          term.examples.some(isChartExample)

        return (
          <motion.div
            key="definition-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            {/* Main Definition Box */}
            <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900/50">
              <div className="prose dark:prose-invert max-w-none">
                {term.definition}
              </div>
            </div>

            {/* Formula at a glance — scannable formula + plain-English restatement.
                Lives here (not on the Calculation tab) so it complements the
                definition without duplicating MetricsCalculation's formula. */}
            {'formulaCallout' in term && term.formulaCallout ? (
              <FormulaCallout callout={term.formulaCallout} />
            ) : null}

            {/* Render Chart Example Conditionally in Definition Tab */}
            {isChartTermWithExample && (
              <div className="mt-6">
                 <h3 className="text-xl font-semibold mb-4">Example Visualization</h3>
                 <ErrorBoundary 
                    key={`chart-example-container-${term.slug}-def`}
                    fallback={(error) => <ExampleRenderingErrorFallback error={error} />}
                 >
                    <Suspense fallback={<div className="text-center p-4">Loading Chart...</div>}> 
                      <GlossaryChartComponent 
                        glossaryTerm={term} 
                        className="rounded-lg border border-gray-200 dark:border-gray-700 p-4"/>
                    </Suspense>
                 </ErrorBoundary>
              </div>
            )}

            {/* Other Sections for Definition Tab */}
            {tabContent.keyPoints && term.keyPoints && <KeyPointsSection points={term.keyPoints} />}
            {tabContent.bestPractices && term.bestPractices && <BestPracticesSection practices={term.bestPractices} />}
            {tabContent.bestUsedFor && term.bestUsedFor && <BestUsedForSection useCases={term.bestUsedFor} />}
            {tabContent.supplementalResources && hasSupplementalResources(term) && <ResourcesSection term={term} />}
          </motion.div>
        )
      }

      case 'calculation':
        // Render the calculation component for any term that EITHER
        // (a) has category === 'metrics' (which always gets the tab
        // even pre-enrichment, with a placeholder) OR (b) carries
        // explicit `calculation` data — the latter covers the Q2 2026
        // Tier-1 enrichment widening that landed formulas on Creative
        // Fatigue + Incrementality (creative + general categories).
        // The MetricsComparison component below receives a term cast
        // to metric-shape regardless of the source category; the cast
        // doesn't affect runtime behavior but lets the component's
        // type-narrowing infer `calculation` is present.
        if (term.category === 'metrics' || ('calculation' in term && Boolean(term.calculation))) {
          // Find the actual calculation to use. Tightened the
          // `'calculation' in term` narrowing to ALSO null-check the
          // resolved value — CreativeTerm and GeneralTerm declare
          // `calculation?` as optional after the Q2 2026 Tier-1
          // enrichment widening, so a true `'calculation' in term`
          // can still resolve to undefined and used to slip past
          // TypeScript.
          const actualCalculation = calculationData ??
            (('calculation' in term && term.calculation) ? term.calculation : {
              formula: 'Loading calculation data...',
              explanation: 'Calculation information is being prepared.',
              operation: 'division' as OperationType,
              components: []
            })

          // NOTE: `formulaCallout` is rendered on the DEFINITION tab (as a
          // scannable "formula at a glance"), NOT here — the `MetricsCalculation`
          // block below already leads with the same tokenised formula, so
          // rendering the callout here too produced two near-identical formula
          // blocks on one tab (no value add). One formula presentation per view.
          return (
            <motion.div
              key="calculation-content"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="space-y-6"
            >
              {/* MetricsComparison now accepts any term with a
                  `calculation` field (was: metric-category only +
                  `as const` category lie). Spread the term and
                  attach the resolved calculation; no cast needed. */}
              <MetricsComparison
                term={{ ...term, calculation: actualCalculation }}
              />
            </motion.div>
          )
        }
        return null

      case 'examples': {
        // Ensure examples exist
        if (!term.examples?.length) {
          return null
        }

        // If it's a chart term, render the GlossaryChartComponent
        if (term.category === 'charts') {
          // Check if there is at least one chart example specifically
          const hasChartExample = term.examples.some(isChartExample)
          if (!hasChartExample) {
             return (
                <motion.div
                  key="examples-content-no-chart-fallback"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="space-y-6"
                >
                   <div className="rounded-xl border border-yellow-200 bg-yellow-50 p-6 dark:border-yellow-900/30 dark:bg-yellow-900/10">
                     <p className="text-sm text-yellow-700 dark:text-yellow-300">No chart visualization examples available for this term.</p>
                   </div>
                </motion.div>
             )
          }
          return (
            <motion.div
              key="examples-content-chart"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="space-y-6"
            >
              <ErrorBoundary 
                // Use a key based on the term slug for stability
                key={`chart-example-container-${term.slug}-ex`}
                fallback={(error) => <ExampleRenderingErrorFallback error={error} />}
              >
                <Suspense fallback={<div className="text-center p-4">Loading Chart...</div>}> 
                  {/* Pass the main term object, GlossaryChart will handle examples */}
                  <GlossaryChartComponent 
                    glossaryTerm={term} 
                    className="rounded-lg border border-gray-200 dark:border-gray-700 p-4"/>
                </Suspense>
              </ErrorBoundary>
            </motion.div>
          )
        }
        
        // If it's NOT a chart term, render non-chart examples using ExampleItems
        else {
           const nonChartExamples = term.examples.filter(ex => !isChartExample(ex))
           if (!nonChartExamples.length) {
             // Handle case where a non-chart term might only have (invalidly placed) chart examples
             return (
                <motion.div
                  key="examples-content-no-text-fallback"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="space-y-6"
                >
                   <div className="rounded-xl border border-yellow-200 bg-yellow-50 p-6 dark:border-yellow-900/30 dark:bg-yellow-900/10">
                     <p className="text-sm text-yellow-700 dark:text-yellow-300">No text examples available for this term.</p>
                   </div>
                </motion.div>
             )
           }
           return (
            <motion.div
              key="examples-content-non-chart"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="space-y-6"
            >
              <ErrorBoundary fallback={(error) => <ExampleRenderingErrorFallback error={error} />}>
                {/* Pass the original term object to ExampleItems */}
                <ExampleItems examples={term.examples} term={term} />
              </ErrorBoundary>
            </motion.div>
          )
        }
      }

      case 'usage': {
        const chartUseCases = isChartTerm(term) ? term.chartUseCases : undefined
        if (!term.bestUsedFor && !(chartUseCases && chartUseCases.length > 0)) return null
        return (
          <motion.div
            key="usage-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            {term.bestUsedFor && <ChartUsageSection items={term.bestUsedFor} />}
            {chartUseCases && <ChartUseCasesSection useCases={chartUseCases} />}
          </motion.div>
        )
      }

      case 'limitations': { // This case handles both direct field and fieldContent
        const limitationsContent = getTermContent(term, 'limitations')
        if (!limitationsContent) return null

        // Render using FieldContentRenderer if available
        if (term.fieldContent?.limitations) {
            return (
                <motion.div
                    key="limitations-field-content"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="space-y-6"
                >
                    <FieldContentRenderer title="Limitations" content={term.fieldContent.limitations} />
                </motion.div>
            )
        }
        // Fallback to ChartLimitationsSection for direct string array field
        if (Array.isArray(limitationsContent)) {
            return (
                <motion.div
                    key="limitations-direct-field"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="space-y-6"
                >
                    <ChartLimitationsSection items={limitationsContent as string[]} />
                </motion.div>
            )
        }
        return null // Should not happen if tabContent.limitations is true, but safety check
      }

      case 'advantages': { // This case handles both direct field and fieldContent
          const advantagesContent = getTermContent(term, 'advantages')
          if (!advantagesContent) return null

          // Render using FieldContentRenderer if available
          if (term.fieldContent?.advantages) {
              return (
                  <motion.div
                      key="advantages-field-content"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                      className="space-y-6"
                  >
                      <FieldContentRenderer title="Advantages" content={term.fieldContent.advantages} />
                  </motion.div>
              )
          }
          // Fallback for direct string array field
          if (Array.isArray(advantagesContent)) {
              // Use FieldContentRenderer with manual STRING_LIST structure for consistency
              return (
                  <motion.div
                      key="advantages-direct-field"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                      className="space-y-6"
                  >
                      <FieldContentRenderer
                          title="Advantages"
                          content={{
                              type: 'STRING_LIST',
                              sectionName: 'advantages',
                              display: { component: 'bullet-list', layout: { compact: true } },
                              items: advantagesContent as string[]
                          }}
                      />
                  </motion.div>
              )
          }
          return null // Safety check
      }

      // Keep remaining specific cases as they were, wrapped in motion.div
      case 'bestPractices':
        if (!term.bestPractices) return null
        return (
          <motion.div
            key="bestPractices-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            <BestPracticesSection practices={term.bestPractices} />
          </motion.div>
        )

      case 'bestUsedFor':
        if (!term.bestUsedFor) return null
        return (
           <motion.div
            key="bestUsedFor-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            <BestUsedForSection useCases={term.bestUsedFor} />
          </motion.div>
        )

      case 'keyPoints':
        if (!term.keyPoints) return null
        return (
           <motion.div
            key="keyPoints-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            <KeyPointsSection points={term.keyPoints} />
          </motion.div>
        )

      case 'comparison':
        if (!networkData) return null
        return (
          <motion.div
            key="comparison-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            <Suspense fallback={<div className="text-center p-4">Loading Metric Network...</div>}> 
              <MetricNetwork networkData={networkData} />
            </Suspense>
          </motion.div>
        )

      case 'supplementalResources':
        if (!hasSupplementalResources(term)) return null
        return (
           <motion.div
            key="supplementalResources-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            <ResourcesSection term={term} />
           </motion.div>
        )

      default:
        // ADD the generic fieldContent check here as a fallback
        if (term.fieldContent?.[activeTab]) {
          return (
            <motion.div
              key={`field-content-${activeTab}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="space-y-6"
            >
              <FieldContentRenderer
                title={activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
                content={term.fieldContent[activeTab]}
              />
            </motion.div>
          )
        }
        // If no specific case and no fieldContent match, return null. The
        // unhandled-tab condition is derived purely and warned from an effect
        // (see `isUnhandledTab` below) — never logged from this render memo.
        return null
    }
  }, [activeTab, term, availableTabs, tabContent, isMounted, calculationData, networkData])

  // Mirror the content memo's "unhandled tab" branch as a PURE derivation so the
  // warning is emitted from an effect rather than during render: a tab is
  // unhandled only if it passed the availability gate, isn't one of the switch
  // cases, and has no generic fieldContent fallback.
  const isUnhandledTab = useMemo(() => {
    if (!availableTabs.includes(activeTab) || !tabContent[activeTab]) return false
    if (HANDLED_TAB_TYPES.has(activeTab)) return false
    return !term.fieldContent?.[activeTab]
  }, [activeTab, availableTabs, tabContent, term])

  useEffect(() => {
    if (isMounted && isUnhandledTab) {
      clientLogger.warn(`Unhandled tab type or missing content for tab: ${activeTab}`, { component: 'TabContent' })
    }
  }, [isMounted, isUnhandledTab, activeTab])

  if (!isMounted) {
    // Render a simple loading state or skeleton during initial mount
    return <div className="animate-pulse p-6 text-center text-gray-500">Loading content...</div>
  }

  // Render the memoized content
  return content
}
